import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api } from '../lib/api.js'
import { cart } from '../lib/cart.js'
import { aud, tidyName } from '../lib/format.js'
import { isInCategory } from '../data/site.js'
import { FINISHES, FINISH_BY_SLUG, FINISH_SWATCHES, finishesOf, isFinishOption } from '../data/finishes.js'
import { imageOption } from '../lib/imageFocus.js'
import Icon from '../components/Icon.jsx'
import ProductImage from '../components/ProductImage.jsx'
import ProductCard from '../components/ProductCard.jsx'

// One page per finish: every hardware item that comes in this colour, priced, with a
// quantity box and an Add button on each line — the way a tradie buys, half a dozen
// clamps and a pair of hinges in one pass, without opening six product pages.

const parseOpts = (v) => { try { return JSON.parse(v.opts) || {} } catch { return {} } }

export default function HardwareFinish() {
  const { finish: slug } = useParams()
  const finish = FINISH_BY_SLUG[slug]
  const [products, setProducts] = useState(null)
  const [variations, setVariations] = useState(null)
  const [basket, setBasket] = useState(() => ({ count: cart.count(), subtotal: cart.subtotal() }))
  // what was just added, announced to screen readers (the row's own "Added" tick is visual only)
  const [announcement, setAnnouncement] = useState('')

  useEffect(() => cart.subscribe(() => setBasket({ count: cart.count(), subtotal: cart.subtotal() })), [])

  useEffect(() => {
    api.products().then((d) => setProducts(d.products || [])).catch(() => setProducts([]))
  }, [])

  // Every finish page draws on the same set of items, so the variation fetch is keyed
  // on the products list, not on the finish — switching colour re-renders, never refetches.
  const finishSkus = useMemo(
    () => (products || []).filter((p) => isInCategory(p, 'hardware') && finishesOf(p).length).map((p) => p.sku),
    [products],
  )
  useEffect(() => {
    if (!products) return
    if (!finishSkus.length) { setVariations([]); return }
    api.variations(finishSkus).then((d) => setVariations(d.variations || [])).catch(() => setVariations([]))
  }, [products, finishSkus.join(',')]) // eslint-disable-line react-hooks/exhaustive-deps

  const items = useMemo(() => {
    if (!products || !finish) return []
    return (products || [])
      .filter((p) => isInCategory(p, 'hardware') && finishesOf(p).includes(finish.name))
      .sort((a, b) => a.sort - b.sort)
  }, [products, finish])

  // The colour-specific packages and sliding kits are separate products named for their
  // finish rather than optioned by it — they belong on this page too, as complete kits.
  const kits = useMemo(() => {
    if (!products || !finish) return []
    return (products || [])
      .filter((p) => isInCategory(p, 'hardware') && !finishesOf(p).length && p.name.includes(finish.name))
      .sort((a, b) => a.sort - b.sort)
  }, [products, finish])

  const switchRow = useRef(null)
  useEffect(() => {
    const active = switchRow.current?.querySelector('.active')
    if (active) switchRow.current.scrollLeft = Math.max(0, active.offsetLeft - 20)
  }, [slug])

  if (!finish) {
    return (
      <main className="container page">
        <p className="muted">That finish doesn&rsquo;t exist. <Link to="/hardware">See all six finishes</Link>.</p>
      </main>
    )
  }

  const loading = products === null || variations === null

  return (
    <main className={`container page ${basket.count > 0 ? 'finish-page-pad' : ''}`}>
      <nav className="breadcrumbs" aria-label="Breadcrumb">
        <Link to="/">Home</Link> / <Link to="/shop">Shop</Link> / <Link to="/shop/hardware">Hardware</Link> /{' '}
        <Link to="/hardware">Finishes</Link> / <span>{finish.heading}</span>
      </nav>

      <div className="finish-banner">
        <h1>{finish.heading} Hardware <span>(Square Series)</span></h1>
      </div>

      <section className="finish-intro">
        <div className="finish-intro-media">
          <img src={finish.hero} alt={`${finish.heading} shower screen hardware range`} width="1200" height="900" />
        </div>
        <div className="finish-intro-copy">
          <h2>Level Up&rsquo;s 10mm shower screen hardware &mdash; {finish.heading}</h2>
          <p>{finish.blurb}</p>
          <p>
            Solid brass, finished with a PVD electro-coating that will not flake or discolour in a
            wet room. Sized for 10mm toughened glass and supplied with all the fixings you need.
          </p>
          <p className="muted">
            Prices below are per unit and exclude GST. Pick up free from Ravenhall, or have it
            delivered within 50 km.
          </p>
        </div>
      </section>

      <div className="finish-switch" role="tablist" aria-label="Hardware finish" ref={switchRow}>
        {FINISHES.map((f) => (
          <Link
            key={f.slug}
            to={`/hardware/${f.slug}`}
            className={`finish-swatch-chip ${f.slug === finish.slug ? 'active' : ''}`}
            aria-current={f.slug === finish.slug ? 'page' : undefined}
          >
            <span className="finish-swatch" style={{ background: FINISH_SWATCHES[f.name] }} aria-hidden="true" />
            {f.name}
          </Link>
        ))}
      </div>

      {loading ? (
        <p className="muted">Loading the {finish.heading.toLowerCase()} range…</p>
      ) : items.length === 0 ? (
        <p className="muted">Nothing is listed in this finish yet. <Link to="/shop/hardware">Browse all hardware</Link>.</p>
      ) : (
        <div className="finish-table-wrap">
          <table className="finish-table">
            <thead>
              <tr>
                <th scope="col" className="col-photo">Product</th>
                <th scope="col">Name</th>
                <th scope="col" className="col-price">Price</th>
                <th scope="col" className="col-qty">Qty</th>
                <th scope="col" className="col-add"><span className="sr-only">Add to cart</span></th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <FinishRow key={p.sku} product={p} finish={finish} variations={variations} onAdd={setAnnouncement} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="sr-only" role="status" aria-live="polite">{announcement}</p>

      {kits.length > 0 && (
        <section className="finish-kits">
          <h2>Complete kits in {finish.heading.toLowerCase()}</h2>
          <p className="muted">Everything for one screen in a single box — cheaper than buying the parts separately.</p>
          <div className="product-grid">
            {kits.map((p) => <ProductCard key={p.sku} product={p} />)}
          </div>
        </section>
      )}

      {basket.count > 0 && (
        <div className="finish-cartbar">
          <div className="container finish-cartbar-inner">
            <span className="finish-cartbar-sum">
              <strong>{basket.count}</strong> item{basket.count === 1 ? '' : 's'} in your cart
              <span className="finish-cartbar-total">{aud(basket.subtotal)} <span className="ex-gst">ex GST</span></span>
            </span>
            <Link className="btn btn-gold" to="/cart">View cart</Link>
          </div>
        </div>
      )}
    </main>
  )
}

function FinishRow({ product, finish, variations, onAdd }) {
  const [qty, setQty] = useState(1)
  const [added, setAdded] = useState(false)
  useEffect(() => { setQty(1); setAdded(false) }, [finish.slug])

  let options = []
  try { options = JSON.parse(product.options || '[]') || [] } catch { /* leave empty */ }
  // Anything with a second choice to make (a length, a hand) can't be a one-tap add —
  // send those to the product page rather than guessing on the customer's behalf.
  const needsConfig = options.filter((o) => !isFinishOption(o) && o.values.length > 1).length > 0
  const finishOptName = options.find(isFinishOption)?.name

  const variation = useMemo(
    () => (variations || []).find(
      (v) => v.product_sku === product.sku && parseOpts(v)[finishOptName] === finish.name,
    ),
    [variations, product.sku, finishOptName, finish.name],
  )

  // This finish's own photo, else a generic untagged one — never another colour's,
  // which would show a chrome hinge on the matte black page.
  const image = useMemo(() => {
    const list = Array.isArray(product.images) ? product.images : []
    return list.find((src) => imageOption(src) === finish.name)
      || list.find((src) => !imageOption(src))
  }, [product.images, finish.name])

  // A configurable item (a size to pick, a hand to choose) has no single row to price
  // here — it keeps its place in the list but sends the customer to its own page.
  const price = needsConfig
    ? (product.min_price ?? product.price ?? null)
    : variation ? (variation.sale_price ?? variation.price) : null
  const onSale = !needsConfig && variation?.sale_price != null
  const stock = variation?.stock
  const soldOut = !needsConfig && (!variation || variation.in_stock === 0 || stock === 0)
  const max = stock == null ? 99 : stock
  const href = `/product/${encodeURIComponent(product.sku)}`

  function add() {
    if (!variation || soldOut) return
    cart.add({
      id: variation.id,
      productSku: product.sku,
      name: product.name,
      opts: parseOpts(variation),
      unitPrice: price,
      qty,
      image: image || '',
    })
    setAdded(true)
    onAdd?.(`${qty} × ${product.name} (${finish.name}) added to your cart`)
    setTimeout(() => setAdded(false), 2400)
  }

  return (
    <tr className={soldOut ? 'row-out' : ''}>
      <td className="col-photo">
        <Link to={href} className="finish-thumb" aria-label={product.name}>
          <ProductImage src={image} category={product.category} alt={product.name} loading="lazy" />
        </Link>
      </td>
      <td>
        <Link to={href} className="finish-item-name">{tidyName(product.name)}</Link>
        <span className="finish-item-meta">
          {product.sku}
          {stock != null && !soldOut && stock <= 5 && <em> · only {stock} left</em>}
        </span>
      </td>
      <td className="col-price">
        {price == null ? (
          <span className="muted">—</span>
        ) : (
          <>
            {needsConfig && <span className="from">From </span>}
            <strong className={onSale ? 'sale' : ''}>{aud(price)}</strong>
            <span className="ex-gst"> ex GST</span>
          </>
        )}
      </td>
      <td className="col-qty">
        {!soldOut && !needsConfig && (
          <div className="qty-input qty-input-sm">
            <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label={`Decrease ${product.name} quantity`}>−</button>
            <span>{qty}</span>
            <button type="button" onClick={() => setQty((q) => Math.min(max, q + 1))} aria-label={`Increase ${product.name} quantity`}>+</button>
          </div>
        )}
      </td>
      <td className="col-add">
        {needsConfig ? (
          <Link className="btn btn-outline btn-sm" to={href}>Choose options</Link>
        ) : soldOut ? (
          <span className="badge badge-out">Out of stock</span>
        ) : (
          <button type="button" className={`btn btn-sm ${added ? 'btn-added' : 'btn-primary'}`} onClick={add}>
            {added ? <><Icon name="check" size={15} /> Added</> : 'Add'}
          </button>
        )}
      </td>
    </tr>
  )
}
